import { ReactNode, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  Link,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SkeletonText,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tile,
} from "@carbon/react";
import { Download } from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { useNotification } from "../../../context";
import { isOcNotFound, useOc } from "../../../hooks/useOc";
import { useOcPdf } from "../../../hooks/useOcActions";
import { PurchaseOrderLine, PurchaseOrderResponse } from "../../../types";
import { formatDate, formatDateTime } from "../../../utils/utils";
import OcStatusPill from "../components/OcStatusPill";
import { formatOrderTotal } from "../formatOrderTotal";
import { isOcOverdue } from "../ocOverdue";
import OcDocumentTab from "./OcDocumentTab";
import OcTimeline from "./OcTimeline";
import { useOcDetailActions } from "./useOcDetailActions";
import "./ocDetail.scss";
import "./ocDetailModal.scss";

const TAB_INTERNAL = 0;
const TAB_PDF = 1;

interface LineRow extends DataTableRow {
  material: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  lineTotal: string;
}

const NONE = "-";

/** A calendar date for display; falls back to the raw value if it can't be parsed. */
const showDate = (value: string | null): string =>
  value ? formatDate(value) || value : NONE;

const formatQuantity = (n: number): string =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(n);

/** Every amount uses the OC's own currency — never summed or converted across currencies. */
const toLineRow = (line: PurchaseOrderLine, currency: string): LineRow => ({
  id: line.id,
  material: line.materialName ?? NONE,
  description: line.description ?? NONE,
  quantity: formatQuantity(line.quantity),
  unit: line.unit ?? NONE,
  unitPrice: formatOrderTotal(line.unitPrice, currency),
  lineTotal: formatOrderTotal(line.lineTotal, currency),
});

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="oc-detail__field">
    <dt className="oc-detail__label">{label}</dt>
    <dd className="oc-detail__value">{children}</dd>
  </div>
);

/** `totalAmount` is the NET; it is only called "net" when VAT is shown beside it. */
const netLabelKey = (oc: PurchaseOrderResponse): string =>
  oc.taxAmount != null
    ? "purchaseOrders.detail.net"
    : "purchaseOrders.detail.total";

/**
 * Any COORDINATION fact present? Deliberately not `deliverySite`: generated OCs (DRAFTs included)
 * already carry a site, which would make every draft show the block. The site is still a field
 * inside the block once it shows.
 */
const hasDeliveryInfo = (oc: PurchaseOrderResponse): boolean =>
  Boolean(
    oc.deliveryState ||
      oc.scheduledDate ||
      oc.scheduledTime ||
      oc.deliveryWindow ||
      oc.remittanceNumber ||
      oc.remittanceFile ||
      oc.receptionDate
  );

const DeliveryBlock = ({ oc }: { oc: PurchaseOrderResponse }) => {
  const { t } = useTranslation();
  const schedule = [
    oc.scheduledDate ? showDate(oc.scheduledDate) : null,
    oc.scheduledTime,
    oc.deliveryWindow,
  ]
    .filter(Boolean)
    .join(" · ");
  const deliverySite = oc.deliverySite
    ? [oc.deliverySite.name, oc.deliverySite.address]
        .filter(Boolean)
        .join(" · ")
    : NONE;

  return (
    <Tile className="oc-detail__tile">
      <h2 className="oc-detail__section-title">
        {t("purchaseOrders.detail.delivery")}
      </h2>
      <dl className="oc-detail__grid">
        <Field label={t("purchaseOrders.detail.deliverySite")}>
          {deliverySite}
        </Field>
        {oc.deliveryState ? (
          <Field label={t("purchaseOrders.detail.deliveryState")}>
            {t(`purchaseOrders.deliveryState.${oc.deliveryState}`, {
              defaultValue: oc.deliveryState,
            })}
          </Field>
        ) : null}
        {schedule ? (
          <Field label={t("purchaseOrders.detail.scheduled")}>{schedule}</Field>
        ) : null}
        {oc.remittanceNumber ? (
          <Field label={t("purchaseOrders.detail.remittanceNumber")}>
            {oc.remittanceNumber}
          </Field>
        ) : null}
        {oc.remittanceFile ? (
          <Field label={t("deliveries.form.remittanceFile")}>
            <Link
              href={oc.remittanceFile}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("deliveries.form.remittanceFileView")}
            </Link>
          </Field>
        ) : null}
        {oc.receptionDate ? (
          <Field label={t("purchaseOrders.detail.receptionDate")}>
            {formatDateTime(oc.receptionDate)}
          </Field>
        ) : null}
      </dl>
    </Tile>
  );
};

/** Detalle interno: the lean summary card, the delivery block (once there is any), the lines. */
const InternalDetail = ({ oc }: { oc: PurchaseOrderResponse }) => {
  const { t } = useTranslation();
  const supplierName = oc.supplier?.name ?? oc.supplier?.companyName ?? NONE;
  const lineRows = useMemo(
    () => oc.lines.map((line) => toLineRow(line, oc.currency)),
    [oc]
  );
  const lineHeaders: DataTableColumn<LineRow>[] = [
    { key: "material", header: t("purchaseOrders.detail.lines.material") },
    {
      key: "description",
      header: t("purchaseOrders.detail.lines.description"),
    },
    { key: "quantity", header: t("purchaseOrders.detail.lines.quantity") },
    { key: "unit", header: t("purchaseOrders.detail.lines.unit") },
    { key: "unitPrice", header: t("purchaseOrders.detail.lines.unitPrice") },
    { key: "lineTotal", header: t("purchaseOrders.detail.lines.lineTotal") },
  ];

  const requestOrigin = !oc.purchaseRequestCode ? (
    NONE
  ) : oc.purchaseRequestId ? (
    <Link as={RouterLink} to={`/purchase-requests/${oc.purchaseRequestId}`}>
      {oc.purchaseRequestCode}
    </Link>
  ) : (
    oc.purchaseRequestCode
  );

  return (
    <div className="oc-detail-modal__panel">
      <Tile className="oc-detail__tile">
        <dl className="oc-detail__grid">
          <Field label={t("purchaseOrders.detail.supplier")}>
            {supplierName}
          </Field>
          <Field label={t("purchaseOrders.detail.contactEmail")}>
            {oc.supplier?.email || NONE}
          </Field>
          <Field label={t("purchaseOrders.detail.purchaseRequest")}>
            {requestOrigin}
          </Field>
          <Field label={t("purchaseOrders.detail.itemsIncluded")}>
            {oc.lines.length}
          </Field>
          <Field label={t("purchaseOrders.detail.paymentCondition")}>
            {oc.paymentTerm || NONE}
          </Field>
          <Field label={t("purchaseOrders.detail.createdAt")}>
            {oc.createdAt ? showDate(oc.createdAt) : NONE}
          </Field>
          <Field label={t("purchaseOrders.detail.requestedDeliveryDate")}>
            {showDate(oc.requestedDeliveryDate)}
            {isOcOverdue(oc) ? (
              <span className="oc-detail-modal__overdue">
                {t("purchaseOrders.detail.overdueMarker")}
              </span>
            ) : null}
          </Field>
        </dl>
      </Tile>

      {hasDeliveryInfo(oc) ? <DeliveryBlock oc={oc} /> : null}

      <div>
        <DataTable
          headers={lineHeaders}
          rows={lineRows}
          isLoading={false}
          empty={<EmptyState title={t("purchaseOrders.detail.lines.empty")} />}
        />
        <p className="oc-detail__total">
          <span>{t(netLabelKey(oc))}</span>{" "}
          <strong>{formatOrderTotal(oc.totalAmount, oc.currency)}</strong>
        </p>
        {oc.taxAmount != null ? (
          <p className="oc-detail__total">
            <span>{t("purchaseOrders.detail.tax")}</span>{" "}
            <strong>{formatOrderTotal(oc.taxAmount, oc.currency)}</strong>
          </p>
        ) : null}
        {oc.grossAmount != null ? (
          <p className="oc-detail__total">
            <span>{t("purchaseOrders.detail.gross")}</span>{" "}
            <strong>{formatOrderTotal(oc.grossAmount, oc.currency)}</strong>
          </p>
        ) : null}
      </div>
    </div>
  );
};

/** Saves the blob as `<name>` through a transient anchor (the technique `OcDocumentTab` uses). */
const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface OcDetailBodyProps {
  ocId: string;
  onClose: () => void;
  /** The status-driven action buttons (primary + "Acciones" menu), shown in the footer. */
  footerActions: ReactNode;
}

const OcDetailContent = ({
  ocId,
  onClose,
  footerActions,
}: OcDetailBodyProps) => {
  const { t } = useTranslation();
  const { data: oc, isLoading, isError, error } = useOc(ocId);
  const [selectedTab, setSelectedTab] = useState(TAB_INTERNAL);
  const { openNotification } = useNotification();
  // The PDF is fetched when the PDF tab opens OR on the footer click; both share one query key, so
  // the bytes are requested at most once (the click reuses cached bytes or an in-flight fetch).
  const pdf = useOcPdf(ocId, selectedTab === TAB_PDF);
  const { data: pdfBlob, refetch: refetchPdf } = pdf;
  const [preparingPdf, setPreparingPdf] = useState(false);

  const downloadPdf = async () => {
    if (!oc) return;
    if (pdfBlob) {
      saveBlob(pdfBlob, `${oc.code}.pdf`);
      return;
    }
    setPreparingPdf(true);
    // refetch() fetches even while the query is disabled; cancelRefetch:false joins an in-flight
    // fetch (PDF tab) instead of restarting it. Its result — not query state — settles the button,
    // so a failed attempt can always be retried.
    const result = await refetchPdf({ cancelRefetch: false });
    setPreparingPdf(false);
    if (result.data) {
      saveBlob(result.data, `${oc.code}.pdf`);
    } else {
      openNotification(t("purchaseOrders.detail.document.loadError"));
    }
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div data-testid="oc-detail-skeleton" aria-busy="true">
        <SkeletonText heading width="30%" />
        <SkeletonText paragraph lineCount={6} />
      </div>
    );
  } else if (isError || !oc) {
    const notFound = isOcNotFound(error);
    body = (
      <EmptyState
        title={t(
          notFound
            ? "purchaseOrders.detail.notFound"
            : "purchaseOrders.detail.loadError"
        )}
        description={
          notFound ? t("purchaseOrders.detail.notFoundDescription") : undefined
        }
      />
    );
  } else {
    body = (
      <>
        <div className="oc-detail-modal__head">
          <span className="oc-detail-modal__code">{oc.code}</span>
          <OcStatusPill status={oc.status} />
        </div>
        <Tabs
          selectedIndex={selectedTab}
          onChange={({ selectedIndex }) => setSelectedTab(selectedIndex)}
        >
          <TabList aria-label={t("purchaseOrders.detail.tabsLabel")}>
            <Tab>{t("purchaseOrders.detail.tabs.internal")}</Tab>
            <Tab>{t("purchaseOrders.detail.tabs.pdf")}</Tab>
            <Tab>{t("purchaseOrders.detail.tabs.history")}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <InternalDetail oc={oc} />
            </TabPanel>
            <TabPanel>
              <OcDocumentTab
                ocId={oc.id}
                ocCode={oc.code}
                active={selectedTab === TAB_PDF}
              />
            </TabPanel>
            <TabPanel>
              {oc.events.length > 0 ? (
                <OcTimeline events={oc.events} />
              ) : (
                <EmptyState title={t("purchaseOrders.detail.noEvents")} />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </>
    );
  }

  return (
    <>
      {/* closeModal is only auto-injected into DIRECT children of ComposedModal, so wire the X here. */}
      <ModalHeader
        title={t("purchaseOrders.detail.title")}
        closeModal={onClose}
      />
      <ModalBody>{body}</ModalBody>
      <ModalFooter className="oc-detail-modal__footer">
        {oc ? (
          <Button
            className="oc-detail-modal__footer-start"
            kind="tertiary"
            renderIcon={preparingPdf ? undefined : Download}
            disabled={preparingPdf}
            onClick={downloadPdf}
          >
            {t(
              preparingPdf
                ? "purchaseOrders.detail.preparingPdf"
                : "purchaseOrders.detail.downloadPdf"
            )}
          </Button>
        ) : null}
        <Button kind="secondary" onClick={onClose}>
          {t("cta.close")}
        </Button>
        {oc ? footerActions : null}
      </ModalFooter>
    </>
  );
};

interface OcDetailModalProps {
  /** The OC to show; `null` keeps the dialog closed (and fetches nothing). */
  ocId: string | null;
  onClose: () => void;
}

// Menus (the "Acciones" overflow) and the action dialogs live outside this modal's DOM subtree but
// inside its React subtree; without this, Carbon's focus-wrap on blur would yank focus back into
// the detail dialog the moment it moves into them.
const FLOATING_SELECTORS = [".cds--menu", ".cds--modal"];

const OcDetailDialog = ({
  ocId,
  onClose,
}: {
  ocId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { data: oc } = useOc(ocId);
  const { footerActions, dialogs } = useOcDetailActions(oc, onClose);
  return (
    <>
      <ComposedModal
        open
        size="lg"
        aria-label={t("purchaseOrders.detail.title")}
        selectorsFloatingMenus={FLOATING_SELECTORS}
        onClose={onClose}
      >
        <OcDetailContent
          ocId={ocId}
          onClose={onClose}
          footerActions={footerActions}
        />
      </ComposedModal>
      {/* Siblings AFTER the detail dialog, never children: see OcDetailActions.dialogs. */}
      {dialogs}
    </>
  );
};

/**
 * The buyer's purchase-order detail as a dialog, opened from the list. Three tabs — Detalle interno
 * (summary card, delivery once known, lines + totals), PDF enviado al proveedor (inline viewer) and
 * Historial — and a footer with "Descargar / imprimir PDF" (works from any tab), Cerrar and the
 * OC's status-driven actions (Enviar, Aprobar, Coordinar entrega … with the rest in an "Acciones"
 * menu; see `useOcDetailActions`). A 404 (foreign/missing OC) shows a not-found state in the body,
 * never a crash.
 */
const OcDetailModal = ({ ocId, onClose }: OcDetailModalProps) => {
  if (ocId == null) return null;
  // Keyed so switching OCs resets the tab, the pending download and any open action dialog.
  return <OcDetailDialog key={ocId} ocId={ocId} onClose={onClose} />;
};

export default OcDetailModal;
